/**
 * `branding` — logo, colours (contrast checked live by the widget's own
 * brandContrast.ts, which the server runs on save too), radius, font, theme
 * and theme. `on_primary` can be left empty: the widget computes black or
 * white.
 *
 * `powered_by` is stored but the widget draws no powered-by line at all, so
 * the switch is not shown (a stored value is kept). The logo is used by the
 * hosted page only; the widget has no logo slot.
 *
 * Contrast results come in two kinds. BLOCKING (body text on the card) is a
 * red field error and stops save. ADVISORY (a brand colour too faint for a
 * focus ring, button text too faint on the button) is an amber note naming
 * the exact colour the widget draws instead; it never stops save or publish.
 * Both carry one-click fixes.
 */
import { useState } from 'react';
import { Field, Segmented } from '../../ui/kit';
import { colourName, type BrandingSuggestion, type MessagePart } from '../../../../diagnosis-widget/src/brandContrast';
import { useDraft } from '../draft';
import { autoOnColor, brandingContrast, type ContrastCheck } from '../validate';
import { SectionCard, usePath } from './common';

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary', hint: 'Buttons and the focus ring.' },
  { key: 'on_primary', label: 'Text on primary', hint: 'Leave empty and the widget picks black or white, whichever reads better on Primary.' },
  { key: 'surface', label: 'Card background' },
  { key: 'text', label: 'Text' },
  {
    key: 'border',
    label: 'Border',
    hint: 'The card outline and the outline around the capture tabs. Decorative, so there is no contrast minimum. Empty: a line matched to your card colours.',
  },
] as const;

/** `#abc` / `#aabbcc` → `#aabbcc` (what a colour input accepts); null for anything else. */
export function hex6(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (/^#[0-9a-f]{6}$/i.test(v)) return v.toLowerCase();
  const m = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(v);
  return m ? `#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`.toLowerCase() : null;
}

function ColorField({ name, label, hint }: { name: (typeof COLOR_FIELDS)[number]['key']; label: string; hint?: string }) {
  const path = `branding.colors.${name}`;
  const { value, set, issues } = usePath<string | null>(path);
  const primary = usePath<string | null>('branding.colors.primary').value;
  const autoValue = name === 'on_primary' && primary && /^#([0-9a-f]{3}){1,2}$/i.test(primary) ? autoOnColor(primary) : null;
  const invalid = value !== null && value !== '' && hex6(value) === null;
  return (
    <Field label={label} hint={hint} issues={issues} optional>
      {(p) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          {/* A colour input shows black for anything that is not #rrggbb, so an
              invalid or half-typed value read as "black" (third live review).
              Now the swatch shows a struck-through empty box instead. */}
          <span className={`swatch-wrap${invalid ? ' is-invalid' : ''}`}>
            <input
              type="color"
              className="swatch"
              aria-label={`${label} colour picker`}
              value={hex6(value) ?? autoValue ?? '#ffffff'}
              onChange={(e) => set(e.target.value)}
            />
            {invalid ? <span className="swatch-strike" aria-hidden="true" /> : null}
          </span>
          <input
            {...p}
            className="input input-mono"
            data-color-field={name}
            value={value ?? ''}
            placeholder={autoValue ? `Automatic (${autoValue})` : 'Widget default'}
            onChange={(e) => set(e.target.value.trim() === '' ? null : e.target.value.trim())}
            spellCheck={false}
          />
          {value ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => set(null)}>
              Reset
            </button>
          ) : null}
        </div>
      )}
    </Field>
  );
}

/** A colour chip plus its hex: the hex carries the meaning, the chip is decoration. */
function ColorRef({ color }: { color: string }) {
  return (
    <span className="color-ref">
      <span className="color-chip" style={{ background: color }} aria-hidden="true" />
      <code>{color}</code>
    </span>
  );
}

function Parts({ parts }: { parts: MessagePart[] }) {
  return (
    <>
      {parts.map((part, i) => (typeof part === 'string' ? <span key={i}>{part}</span> : <ColorRef key={i} color={part.color} />))}
    </>
  );
}

function statusTag(c: ContrastCheck): { cls: string; text: string } {
  if (c.pair === 'border_on_surface') return { cls: 'ok', text: 'decorative' };
  if (c.status === 'blocking') return { cls: 'bad', text: `needs ${c.required}` };
  if (c.status === 'advisory') return { cls: 'warn', text: 'auto-corrected' };
  return { cls: 'ok', text: c.auto ? `auto · ≥ ${c.required}` : `≥ ${c.required}` };
}

function ContrastNote({
  check,
  keepLabel,
  dismissed,
  onDismiss,
  onUndismiss,
  onApply,
}: {
  check: ContrastCheck;
  keepLabel: string;
  dismissed: boolean;
  onDismiss: () => void;
  onUndismiss: () => void;
  onApply: (s: BrandingSuggestion) => void;
}) {
  const blocking = check.status === 'blocking';
  const drawn = check.rendered[0]?.color;
  if (dismissed && !blocking) {
    return (
      <div className="notice contrast-note" data-pair={check.pair}>
        <div className="grow">
          <strong>Kept as entered.</strong> The widget draws {drawn ? <ColorRef color={drawn} /> : 'a contrast-safe colour'} where it needs to.{' '}
          <button type="button" className="btn-link" onClick={onUndismiss}>
            Show options
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={`notice contrast-note ${blocking ? 'bad' : 'warn'}`} data-pair={check.pair}>
      <span aria-hidden="true">{blocking ? '●' : '▲'}</span>
      <div className="grow stack-sm">
        <div>
          <strong>{blocking ? 'Blocks saving: ' : 'Won’t block publishing: '}</strong>
          <Parts parts={check.parts} />
        </div>
        <div className="row contrast-actions">
          {check.suggestions.map((s) => (
            <button key={s.label} type="button" className="btn btn-sm" onClick={() => onApply(s)}>
              {Object.values(s.changes).map((c) => (
                <span key={c} className="color-chip" style={{ background: c }} aria-hidden="true" />
              ))}
              {s.label}
            </button>
          ))}
          {blocking ? null : (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onDismiss}>
              {keepLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function BrandingSection() {
  const draft = useDraft();
  const logo = usePath<string | null>('branding.logo_url');
  const radius = usePath<number | null>('branding.radius_px');
  const font = usePath<string>('branding.font_family');
  const theme = usePath<'auto' | 'light' | 'dark'>('branding.theme');
  const colors = draft.edited.branding.colors;
  const contrast = brandingContrast(draft.edited.branding);
  // Notes the partner chose to keep, per exact colour pair: a new colour
  // brings its note back.
  const [kept, setKept] = useState<string[]>([]);
  const [applied, setApplied] = useState<{ text: string; colors: string } | null>(null);
  const keyOf = (c: ContrastCheck) => `${c.pair}|${c.foreground}|${c.background}`;
  const notes = contrast.checks.filter((c) => c.status !== 'ok');
  // A pair with nothing set is the widget's own tested palette: no row for it.
  const rows = contrast.checks.filter((c) => c.ratio !== null);

  const apply = (s: BrandingSuggestion) => {
    for (const [field, value] of Object.entries(s.changes)) draft.update(`branding.colors.${field}`, value);
    setApplied({
      text: `Applied: ${Object.entries(s.changes)
        .map(([field, value]) => `${field === 'on_primary' ? 'text on primary' : field} is now ${value}`)
        .join(', ')}. The preview updates in a moment.`,
      colors: JSON.stringify({ ...colors, ...s.changes }),
    });
    const first = Object.keys(s.changes)[0];
    document.querySelector<HTMLInputElement>(`[data-color-field="${first}"]`)?.focus();
  };

  return (
    <SectionCard id="sec-branding" title="Branding" description="How the widget looks on your site. Empty fields keep the widget’s own style.">
      <Field label="Logo URL" hint="An https:// image for the top of your hosted page. The widget itself doesn’t show a logo." issues={logo.issues} optional>
        {(p) => (
          <input
            {...p}
            className="input"
            inputMode="url"
            value={logo.value ?? ''}
            placeholder="https://www.example.com/logo.svg"
            onChange={(e) => logo.set(e.target.value.trim() === '' ? null : e.target.value.trim())}
          />
        )}
      </Field>
      <div className="grid-2">
        {COLOR_FIELDS.map((f) => (
          <ColorField key={f.key} name={f.key} label={f.label} hint={'hint' in f ? f.hint : undefined} />
        ))}
      </div>
      <div className="sunk">
        <div className="label" style={{ marginBottom: 4 }}>
          Contrast (checked the same way on save)
        </div>
        {rows.length === 0 ? <p className="small muted">Set a colour above and its contrast is checked here.</p> : null}
        {rows.map((c) => {
          const tag = statusTag(c);
          return (
            <div className="contrast-row" key={c.pair}>
              <span>{c.label}</span>
              <span className="num muted">{`${c.ratio}:1`}</span>
              <span className={`tag ${tag.cls}`}>{tag.text}</span>
            </div>
          );
        })}
        <div className="stack-sm contrast-notes" role="status" aria-live="polite">
          {notes.map((c) => (
            <ContrastNote
              key={c.pair}
              check={c}
              keepLabel={
                c.pair === 'primary_on_surface'
                  ? `Keep my ${colors.primary ? colourName(colors.primary) : 'colour'}, use the auto outline`
                  : c.pair === 'text_on_surface'
                    ? 'Keep Text blank, use the widget’s pick'
                    : 'Keep mine, let the widget switch it'
              }
              dismissed={kept.includes(keyOf(c))}
              onDismiss={() => setKept((k) => [...k, keyOf(c)])}
              onUndismiss={() => setKept((k) => k.filter((x) => x !== keyOf(c)))}
              onApply={apply}
            />
          ))}
          {applied && applied.colors === JSON.stringify(colors) ? <p className="small muted">{applied.text}</p> : null}
        </div>
      </div>
      <div className="grid-2">
        <Field label="Corner radius (px)" hint="0–64. Empty keeps the widget’s 24 px." issues={radius.issues} optional>
          {(p) => (
            <input
              {...p}
              className="input num"
              type="number"
              min={0}
              max={64}
              value={radius.value ?? ''}
              onChange={(e) => radius.set(e.target.value === '' ? null : Number(e.target.value))}
            />
          )}
        </Field>
        <Field
          label="Font"
          hint="“inherit” uses your page’s font, “system” the widget’s own; or a font your page already loads, e.g. “Inter, sans-serif”."
          issues={font.issues}
        >
          {(p) => <input {...p} className="input" value={font.value} onChange={(e) => font.set(e.target.value)} />}
        </Field>
      </div>
      <div className="field">
        <span className="label">Theme</span>
        <Segmented
          label="Theme"
          value={theme.value}
          onChange={theme.set}
          options={[
            { value: 'auto', label: 'Follow visitor' },
            { value: 'light', label: 'Always light' },
            { value: 'dark', label: 'Always dark' },
          ]}
        />
      </div>
    </SectionCard>
  );
}
